import { useState } from 'react';
import {
  Input,
  Badge,
  Alert,
  Button,
  ButtonGroup,
  Spinner,
} from 'reactstrap';
import { PageHeader } from '../../components/PageHeader';
import { DataTable } from '../../components/DataTable';
import { RelativeTime } from '../../components/RelativeTime';
import { HasPermission } from '../../rbac/HasPermission';
import { useHasPermission } from '../../rbac/useHasPermission';
import {
  useNexusPlugins,
  useUpdateNexusPlugin,
  useProbeNexusPlugin,
} from './useNexusPlugins';
import { PluginCredentialsModal } from './PluginCredentialsModal';
import { PluginConfigModal } from './PluginConfigModal';

/**
 * Nexus Plugin-Registry (Spec 1 von 8).
 *
 * Spec: lmm-wiki/wiki/orbit-specs/nexus-plugin-config.md
 * Endpoints: /v1/admin/plugins (siehe NEXUS/endpoints/admin#Plugins)
 *
 * Permission: per INBOX akzeptiert Nexus ausschließlich M2M. Auth läuft
 * server-side via BFF-Proxy. Frontend gated trotzdem mit
 * `integrations.write` weil das die nächstliegende Operator-Permission ist.
 */
export function NexusPluginsPage() {
  const query = useNexusPlugins();
  const updateMutation = useUpdateNexusPlugin();
  const probeMutation = useProbeNexusPlugin();
  const canWrite = useHasPermission({ perms: ['integrations.write'] });

  const [credModalPlugin, setCredModalPlugin] = useState(null);
  const [configModalPlugin, setConfigModalPlugin] = useState(null);
  const [probeResults, setProbeResults] = useState({});

  const plugins = query.data?.plugins ?? [];

  const onToggleEnabled = async (plugin) => {
    await updateMutation.mutateAsync({
      id: plugin.identifier,
      patch: { enabled: !plugin.enabled },
    });
  };

  const onToggleDefault = async (plugin) => {
    await updateMutation.mutateAsync({
      id: plugin.identifier,
      patch: { is_default: !plugin.is_default },
    });
  };

  const onProbe = async (plugin) => {
    setProbeResults((prev) => ({ ...prev, [plugin.identifier]: { pending: true } }));
    try {
      const res = await probeMutation.mutateAsync({ id: plugin.identifier });
      setProbeResults((prev) => ({ ...prev, [plugin.identifier]: res }));
    } catch (err) {
      setProbeResults((prev) => ({
        ...prev,
        [plugin.identifier]: { ok: false, detail: err?.message ?? 'Probe fehlgeschlagen' },
      }));
    }
  };

  return (
    <div>
      <PageHeader
        title="Nexus · Plugin-Registry"
        subtitle="Provider-Plugins für Domain / Hosting / Game / VPS / Cloud · GET /v1/admin/plugins"
      />

      <Alert color="info" className="small">
        Plugins steuern die Provider-Integrationen für Nexus-Provisioning.
        Toggle <code>enabled</code> deaktiviert ein Plugin global; pro Tenant
        kann ein Routing-Override greifen (siehe Tenant-Routing-Page).
        Probe ruft eine harmlose Plugin-Methode auf und meldet Latenz + Status.
      </Alert>

      <DataTable
        loading={query.isLoading}
        error={query.error}
        empty={!query.isLoading && plugins.length === 0}
        emptyMessage="Keine Plugins im Backend registriert."
        onRetry={() => query.refetch()}
      >
        <thead>
          <tr>
            <th>Plugin</th>
            <th style={{ width: 90 }}>Kind</th>
            <th style={{ width: 100 }}>Enabled</th>
            <th style={{ width: 100 }}>Default</th>
            <th style={{ width: 90 }}>Loaded</th>
            <th style={{ width: 150 }}>Last Update</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {plugins.map((p) => {
            const probe = probeResults[p.identifier];
            return (
              <tr key={p.identifier}>
                <td>
                  <div className="fw-semibold">{p.identifier}</div>
                  {probe && <ProbeRow probe={probe} />}
                </td>
                <td>
                  <Badge color="secondary" pill className="orbit-code-badge-sm">{p.kind}</Badge>
                </td>
                <td>
                  <ToggleSwitch
                    id={`enabled-${p.identifier}`}
                    checked={!!p.enabled}
                    onChange={() => onToggleEnabled(p)}
                    disabled={!canWrite || updateMutation.isPending}
                  />
                </td>
                <td>
                  <ToggleSwitch
                    id={`default-${p.identifier}`}
                    checked={!!p.is_default}
                    onChange={() => onToggleDefault(p)}
                    disabled={!canWrite || updateMutation.isPending}
                  />
                </td>
                <td>
                  {p.loaded ? (
                    <Badge color="success" pill>geladen</Badge>
                  ) : (
                    <Badge color="secondary" pill>nicht geladen</Badge>
                  )}
                </td>
                <td>
                  {p.updated_at ? <RelativeTime iso={p.updated_at} /> : <span className="text-muted">—</span>}
                </td>
                <td className="text-end">
                  {canWrite && (
                    <ButtonGroup size="sm">
                      <Button
                        color="info"
                        outline
                        onClick={() => onProbe(p)}
                        disabled={probeMutation.isPending}
                        title="Smoke-Call gegen den Provider"
                      >
                        {probe?.pending ? <Spinner size="sm" /> : '🔌 Probe'}
                      </Button>
                      <Button
                        color="primary"
                        outline
                        onClick={() => setCredModalPlugin(p)}
                      >
                        🔑 Credentials
                      </Button>
                      <Button
                        color="secondary"
                        outline
                        onClick={() => setConfigModalPlugin(p)}
                      >
                        ⚙️ Config
                      </Button>
                    </ButtonGroup>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </DataTable>

      {!canWrite && (
        <Alert color="secondary" className="small mt-3 mb-0">
          Nur-Lese-Modus — Permission <code>integrations.write</code> fehlt.
        </Alert>
      )}

      <HasPermission perms={['integrations.write']}>
        {credModalPlugin && (
          <PluginCredentialsModal
            plugin={credModalPlugin}
            onClose={() => setCredModalPlugin(null)}
          />
        )}
        {configModalPlugin && (
          <PluginConfigModal
            plugin={configModalPlugin}
            onClose={() => setConfigModalPlugin(null)}
          />
        )}
      </HasPermission>
    </div>
  );
}

function ToggleSwitch({ id, checked, onChange, disabled }) {
  return (
    <div className="form-check form-switch mb-0">
      <Input
        type="switch"
        role="switch"
        id={id}
        checked={checked}
        onChange={onChange}
        disabled={disabled}
      />
    </div>
  );
}

function ProbeRow({ probe }) {
  if (probe.pending) {
    return <div className="small text-muted">Probe läuft…</div>;
  }
  if (probe.ok) {
    return (
      <div className="small text-success">
        ✓ {probe.latency_ms ?? '—'} ms · {probe.detail ?? 'OK'}
      </div>
    );
  }
  return (
    <div className="small text-danger">
      ✗ {probe.detail ?? 'Probe fehlgeschlagen'}
    </div>
  );
}
